import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { CheckCircle, PlayCircle, PauseCircle, Wifi, WifiOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventIcon = {
  completed: <CheckCircle className="h-4 w-4 text-green-600" />,
  started: <PlayCircle className="h-4 w-4 text-[#1d4ed8]" />,
  paused: <PauseCircle className="h-4 w-4 text-amber-500" />,
};

const eventLabel = {
  completed: 'Completed',
  started: 'Started',
  paused: 'Paused',
};

const formatTimeAgo = (ts) => {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const ActivityFeed = ({ wsEvents = [], connected = false }) => {
  const { token } = useAuth();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
  }, []);

  // Merge WebSocket events into the feed
  useEffect(() => {
    if (wsEvents.length === 0) return;
    const latest = wsEvents[0];
    setActivity((prev) => {
      const newItem = {
        session_id: latest.session_id || '',
        qr_code_id: latest.qr_code_id || '',
        form_title: latest.form_title || '',
        event: latest.type === 'form_completed' ? 'completed' : 'started',
        timestamp: latest.completed_at || new Date().toISOString(),
        responses_count: latest.total_responses || 0,
      };
      return [newItem, ...prev].slice(0, 30);
    });
  }, [wsEvents]);

  const fetchActivity = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivity(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 border border-[#E2E8F0]" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Live Activity
        </h2>
        <span
          data-testid="ws-status"
          className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
            connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
          {connected ? 'Live' : 'Offline'}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-[#1d4ed8]" />
        </div>
      ) : activity.length === 0 ? (
        <p className="text-sm text-[#475569] text-center py-6">No activity yet</p>
      ) : (
        <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
          {activity.map((item, i) => (
            <div
              key={`${item.session_id}-${i}`}
              className="flex items-start gap-3 text-sm"
              data-testid={`activity-item-${i}`}
            >
              <div className="mt-0.5">{eventIcon[item.event] || eventIcon.started}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.form_title}</p>
                <p className="text-xs text-[#475569]">
                  {eventLabel[item.event] || item.event}
                  {item.responses_count > 0 && ` · ${item.responses_count} responses`}
                </p>
              </div>
              <span className="text-xs text-[#94A3B8] whitespace-nowrap">
                {formatTimeAgo(item.timestamp)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
